import { useRef, useMemo } from "react";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { Check } from "lucide-react";
import { CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { ReactNode } from "react";
import type { ProcessedAssignment } from "../type";
import type { MachineScheduleGantt } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import {
  SCHEDULE_CONSTANTS,
  getColWidth,
  getTechLaneHeight,
  calculateLanesForSpanningView,
  getSpanningBarPosition,
  ZOOM_MIN,
  ZOOM_MAX,
  getJobBarColorForTime,
  groupByTimeOverlap,
  getDayBgClass,
  getHolidayName,
  isWeekendDay,
  type TimePositionedItem
} from "../utils";
import { useHolidays } from "@/hooks/use-holidays";
import type { AvailabilityMap } from "@/hooks/use-resource-availability";
import { AvailabilityBands } from "./availability-bands";
import {
  SpanningGridLines,
  DayColumnBackdrop,
  ClippedIndicator,
  getAssignmentBarClasses,
  OverflowIndicatorCard
} from "./schedule-grid-components";
import { ScheduleBarTooltip } from "./schedule-hover-card";
import { TechnicianBarContent } from "./weekly-technician-grid";
import MonthlyCalendarGrid from "./monthly-calendar-grid";
import type { MonthlyCalendarRow } from "../type";

dayjs.extend(isToday);

const DAY_HEADER_H = 72;
const SHOP_HEADER_H = 36;

// â”€â”€ Weekly detail-grid (100% zoom) constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const WEEK_RULER_W = 48;
const WEEK_DETAIL_HOUR_H = 40; // px per hour (24h Ã— 40 = 960px)
const WEEK_TOTAL_HOURS = 24; // full 24-hour day
const WEEK_DETAIL_H = WEEK_TOTAL_HOURS * WEEK_DETAIL_HOUR_H; // 960px

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ProcessedMachine extends MachineScheduleGantt {
  assignedJobsWithPriority: ProcessedAssignment[];
  laneCount: number;
}

// â”€â”€ MachineRowCard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function MachineRowCard({
  machine,
  isSelected,
  onClick,
  height
}: {
  machine: ProcessedMachine;
  isSelected: boolean;
  onClick: () => void;
  height: number;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "group aero:flex-shrink-0 aero:py-4 aero:pl-4 aero:pr-4 aero:bg-white aero:transition-colors",
        "aero:flex aero:flex-col aero:gap-[9px] aero:cursor-pointer aero:w-full",
        isSelected ? "aero:!bg-brand-primary-50" : "aero:hover:bg-muted/40"
      )}
      style={{ height }}
    >
      {/* Checkbox */}
      <div
        className={cn(
          "aero:w-4 aero:h-4 aero:rounded-sm aero:border aero:flex aero:items-center aero:justify-center aero:transition-all aero:flex-shrink-0",
          isSelected
            ? "aero:border-brand-primary-600 aero:bg-brand-primary-600 aero:opacity-100"
            : "aero:border-neutral-400 aero:bg-white aero:opacity-0 group-hover:aero:opacity-100"
        )}
      >
        {isSelected && <Check className="aero:h-3 aero:w-3 aero:text-white" />}
      </div>
      <div>
        <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
          Machine Name
        </p>
        <p
          className="aero:font-bold aero:text-[14px] aero:leading-[20px] aero:truncate"
          style={{ color: "#1A6B52" }}
          title={machine.machineName}
        >
          {machine.machineName}
        </p>
      </div>
      {machine.shopName && (
        <div>
          <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
            Shop which machine belongs to
          </p>
          <p
            className="aero:text-[12px] aero:font-normal aero:leading-[16px] aero:text-neutral-900 aero:truncate"
            title={machine.shopName}
          >
            {machine.shopName}
          </p>
        </div>
      )}
    </div>
  );
}

// â”€â”€ Row item type â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type RowItem =
  | {
      kind: "shop-header";
      shopName: string;
      machineIds: number[];
      isAnySelected: boolean;
    }
  | {
      kind: "machine";
      machine: ProcessedMachine;
      rowHeight: number;
      assignmentsWithLanes: (ProcessedAssignment & { lane?: number })[];
      maxLane: number;
      isSelected: boolean;
    };

// â”€â”€ WeeklyMachineGrid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface WeeklyMachineGridProps {
  days: Date[];
  machines: ProcessedMachine[];
  selectedMachineIds: number[];
  onMachineSelect: (ids: number[]) => void;
  zoomLevel?: number;
  rowLabelHeader?: string;
  rowLabelDescription?: string;
  headerAction?: ReactNode;
  formatTime: (date: string | null | undefined) => string;
  /** Per-machine AM/PM availability for the calendar bands (WOR-2187). */
  availabilityMap?: AvailabilityMap;
}

export default function WeeklyMachineGrid({
  days,
  machines,
  selectedMachineIds,
  onMachineSelect,
  zoomLevel = ZOOM_MIN,
  rowLabelHeader = "Machines",
  rowLabelDescription,
  headerAction,
  formatTime,
  availabilityMap
}: WeeklyMachineGridProps) {
  const holidays = useHolidays();
  const leftBodyRef = useRef<HTMLDivElement>(null);
  const rightBodyRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);

  const leftPanelWidth = SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH;
  const colWidth = getColWidth("weekly", zoomLevel);
  const laneHeight = getTechLaneHeight(zoomLevel);
  const ROW_PAD = SCHEDULE_CONSTANTS.ROW_PAD;
  const showDetailGrid = zoomLevel >= ZOOM_MAX;
  const GRID_W = (showDetailGrid ? WEEK_RULER_W : 0) + colWidth * days.length;

  const selectedSet = useMemo(
    () => new Set(selectedMachineIds),
    [selectedMachineIds]
  );

  const rowItems = useMemo<RowItem[]>(() => {
    const groups: Record<string, ProcessedMachine[]> = {};
    const groupOrder: string[] = [];
    for (const machine of machines) {
      const shop = machine.shopName || "Unassigned";
      if (!groups[shop]) {
        groups[shop] = [];
        groupOrder.push(shop);
      }
      groups[shop].push(machine);
    }
    const items: RowItem[] = [];
    for (const shopName of groupOrder) {
      const machineIds = groups[shopName].map((m) => m.machineId);
      const isAnySelected = machineIds.some((id) => selectedSet.has(id));
      items.push({ kind: "shop-header", shopName, machineIds, isAnySelected });
      for (const machine of groups[shopName]) {
        const isSelected = selectedSet.has(machine.machineId);
        const { assignments: assignmentsWithLanes, maxLane } =
          calculateLanesForSpanningView(machine.assignedJobsWithPriority);
        const rowHeight = Math.max(
          (maxLane + 1) * laneHeight + ROW_PAD * 2,
          laneHeight + ROW_PAD * 2,
          SCHEDULE_CONSTANTS.SIDEBAR_ROW_MIN_HEIGHT
        );
        items.push({
          kind: "machine",
          machine,
          rowHeight,
          assignmentsWithLanes,
          maxLane,
          isSelected
        });
      }
    }
    return items;
  }, [machines, selectedSet, laneHeight, ROW_PAD, showDetailGrid]);

  // Precompute per-day assignment buckets for the unified detail view.
  // Keyed by day ISO string so days.map() is O(1) lookup instead of O(rowsÃ—assignments).
  const perDayAssignments = useMemo<
    Map<string, (ProcessedAssignment & { lane?: number })[]>
  >(() => {
    if (!showDetailGrid) return new Map();
    const allAssignments = rowItems
      .filter(
        (item): item is Extract<RowItem, { kind: "machine" }> =>
          item.kind === "machine"
      )
      .flatMap((item) => item.assignmentsWithLanes);
    const map = new Map<string, (ProcessedAssignment & { lane?: number })[]>();
    for (const a of allAssignments) {
      if (!a.startDate || !a.eddDate) continue;
      const start = dayjs(a.startDate);
      const end = dayjs(a.eddDate);
      // Add the assignment to every day it spans
      for (const day of days) {
        const d = dayjs(day);
        if (!start.isAfter(d.endOf("day")) && !end.isBefore(d.startOf("day"))) {
          const key = d.toISOString();
          if (!map.has(key)) map.set(key, []);
          map.get(key)!.push(a);
        }
      }
    }
    return map;
  }, [showDetailGrid, rowItems, days]);

  return (
    <CardContent className="aero:p-0">
      <div
        className="aero:flex aero:overflow-hidden"
        style={{ height: "calc(100vh - 320px)" }}
      >
        {/* LEFT */}
        <div
          className="aero:flex-shrink-0 aero:flex aero:flex-col aero:h-full aero:border-r aero:border-neutral-300"
          style={{ width: leftPanelWidth }}
        >
          <div
            className="aero:flex-shrink-0 aero:bg-neutral-50 aero:border-t aero:border-b aero:border-neutral-300 aero:py-3 aero:px-5 aero:flex aero:flex-col aero:gap-1 aero:justify-center"
            style={{ height: 92 }}
          >
            <div className="aero:flex aero:items-center aero:justify-between">
              <span className="aero:font-bold aero:text-sm aero:text-neutral-500 aero:leading-5">
                {rowLabelHeader}
              </span>
              {headerAction}
            </div>
            {rowLabelDescription && (
              <p className="aero:text-[10px] aero:font-normal aero:text-neutral-500 aero:leading-4">
                {rowLabelDescription}
              </p>
            )}
          </div>

          <div
            ref={leftBodyRef}
            className="aero:flex-1 aero:overflow-y-auto aero:overflow-x-hidden"
            onScroll={() => {
              if (isSyncingScroll.current) return;
              isSyncingScroll.current = true;
              if (rightBodyRef.current && leftBodyRef.current) {
                rightBodyRef.current.scrollTop = leftBodyRef.current.scrollTop;
              }
              requestAnimationFrame(() => {
                isSyncingScroll.current = false;
              });
            }}
          >
            {rowItems.map((item, idx) => {
              if (item.kind === "shop-header") {
                return (
                  <div
                    key={`sh-${item.shopName}-${idx}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      const union = Array.from(
                        new Set([...selectedMachineIds, ...item.machineIds])
                      );
                      onMachineSelect(union);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const union = Array.from(
                          new Set([...selectedMachineIds, ...item.machineIds])
                        );
                        onMachineSelect(union);
                      }
                    }}
                    className={cn(
                      "aero:flex aero:items-center aero:justify-between aero:px-4 aero:border-b aero:border-neutral-300 aero:cursor-pointer aero:transition-colors",
                      idx === 0 && "aero:border-t aero:border-neutral-300",
                      item.isAnySelected
                        ? "aero:bg-brand-primary-50"
                        : "aero:bg-neutral-50 aero:hover:bg-neutral-100"
                    )}
                    style={{ height: SHOP_HEADER_H }}
                  >
                    <span className="aero:font-bold aero:text-[12px] aero:leading-[16px] aero:text-neutral-700 aero:truncate">
                      {item.shopName}
                    </span>
                  </div>
                );
              }
              return (
                <div
                  key={item.machine.machineId}
                  className={cn(
                    "aero:relative",
                    idx === 0 && "aero:border-t aero:border-neutral-300"
                  )}
                  style={{ height: item.rowHeight }}
                >
                  <MachineRowCard
                    machine={item.machine}
                    isSelected={item.isSelected}
                    height={item.rowHeight}
                    onClick={() => {
                      const id = item.machine.machineId;
                      if (selectedSet.has(id)) {
                        onMachineSelect(
                          selectedMachineIds.filter((x) => x !== id)
                        );
                      } else {
                        onMachineSelect([...selectedMachineIds, id]);
                      }
                    }}
                  />
                  <div className="aero:pointer-events-none aero:absolute aero:left-0 aero:right-0 aero:bottom-0 aero:h-px aero:bg-neutral-300" />
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — single scroll container; sticky header avoids scrollbar-width sync bug */}
        <div className="aero:flex-1 aero:flex aero:flex-col aero:h-full aero:overflow-hidden aero:pl-5 aero:pt-5">
          <div
            ref={rightBodyRef}
            className="aero:flex-1 aero:overflow-auto"
            onScroll={() => {
              if (isSyncingScroll.current) return;
              isSyncingScroll.current = true;
              if (leftBodyRef.current && rightBodyRef.current) {
                leftBodyRef.current.scrollTop = rightBodyRef.current.scrollTop;
              }
              requestAnimationFrame(() => {
                isSyncingScroll.current = false;
              });
            }}
          >
            <div
              className="aero:w-full aero:relative aero:min-h-full"
              style={{ minWidth: showDetailGrid ? GRID_W : undefined }}
            >
              {/* Full-height weekend/holiday column backdrop behind all rows (WOR-2187 R1) */}
              {!showDetailGrid && <DayColumnBackdrop days={days} />}
              {/* Sticky day header */}
              <div
                className="aero:sticky aero:top-0 aero:z-10 aero:bg-white aero:border-b aero:border-t aero:border-neutral-300"
                style={{ height: DAY_HEADER_H }}
              >
                <div className="aero:flex aero:h-full aero:w-full">
                  {/* Ruler spacer — aligns day header with body ruler column */}
                  {showDetailGrid && (
                    <div
                      className="aero:flex-shrink-0 aero:border-r aero:border-neutral-200"
                      style={{ width: WEEK_RULER_W }}
                    />
                  )}
                  {days.map((day) => {
                    const isTodayDay = dayjs(day).isToday();
                    const holidayName = getHolidayName(day, holidays);
                    const isHoliday = !!holidayName;
                    const isWeekend = isWeekendDay(day);
                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          "aero:pt-1 aero:pr-2 aero:pb-4 aero:pl-2 aero:border-r aero:border-neutral-300 aero:last:border-r-0",
                          // Precedence today > holiday > weekend (WOR-2187).
                          isTodayDay
                            ? "aero:bg-primary/10"
                            : isHoliday
                              ? "aero:bg-yellow-200"
                              : isWeekend
                                ? "aero:bg-gray-200"
                                : ""
                        )}
                        style={{ flex: 1, minWidth: colWidth }}
                      >
                        <p
                          className={cn(
                            "aero:text-[10px] aero:font-semibold aero:uppercase aero:tracking-widest aero:leading-none aero:mb-1",
                            isTodayDay
                              ? "aero:text-primary"
                              : "aero:text-muted-foreground"
                          )}
                        >
                          {dayjs(day).format("ddd")}
                        </p>
                        <p
                          className={cn(
                            "aero:text-xl aero:font-bold aero:leading-none",
                            isTodayDay
                              ? "aero:text-primary"
                              : "aero:text-foreground"
                          )}
                        >
                          {dayjs(day).format("D")}
                        </p>
                        {isHoliday && (
                          <p
                            className="aero:mt-1 aero:text-[10px] aero:font-medium aero:leading-tight aero:text-amber-700 aero:truncate"
                            title={holidayName}
                          >
                            {holidayName}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Body rows â€” 100% zoom: ONE inline ruler + unified 960px time canvas for all resources */}
              {showDetailGrid && (
                <div className="aero:flex" style={{ height: WEEK_DETAIL_H }}>
                  {/* ONE time ruler â€” inline, not sticky, 00:00â€“23:00 */}
                  <div
                    className="aero:flex-shrink-0 aero:bg-white aero:border-r aero:border-neutral-200"
                    style={{
                      width: WEEK_RULER_W,
                      height: WEEK_DETAIL_H,
                      position: "relative"
                    }}
                  >
                    {Array.from({ length: WEEK_TOTAL_HOURS }, (_, h) => (
                      <span
                        key={h}
                        className="aero:absolute aero:text-[9px] aero:leading-none aero:text-neutral-400 aero:select-none"
                        style={{
                          top: Math.max(h * WEEK_DETAIL_HOUR_H - 5, 0),
                          left: 4,
                          width: 36
                        }}
                      >
                        {`${String(h).padStart(2, "0")}:00`}
                      </span>
                    ))}
                  </div>
                  {/* Day columns â€” ALL resources' bars unified in one 960px time space */}
                  {days.map((day) => {
                    const allDayAssignments =
                      perDayAssignments.get(dayjs(day).toISOString()) ?? [];
                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          "aero:relative aero:flex-shrink-0 aero:border-r aero:border-neutral-200 aero:last:border-r-0",
                          // Weekend grey / holiday yellow base in the 100%-zoom detail grid (WOR-2187).
                          getDayBgClass(day, holidays)
                        )}
                        style={{
                          flex: 1,
                          minWidth: colWidth,
                          height: WEEK_DETAIL_H
                        }}
                      >
                        {Array.from(
                          { length: WEEK_TOTAL_HOURS + 1 },
                          (_, i) => (
                            <div
                              key={i}
                              className="aero:absolute aero:left-0 aero:right-0 aero:border-t aero:border-neutral-100"
                              style={{ top: i * WEEK_DETAIL_HOUR_H }}
                            />
                          )
                        )}
                        {(() => {
                          // â”€â”€ Overlap-aware rendering (Figma spec: max 2 visible + +N card) â”€â”€
                          const OUTER_PAD = 2;
                          const INNER_GAP = 2;
                          const OVERFLOW_W = 40;

                          type PositionedA = TimePositionedItem<
                            (typeof allDayAssignments)[number] & {
                              _aIdx: number;
                            }
                          >;

                          const positioned: PositionedA[] = [];
                          for (
                            let aIdx = 0;
                            aIdx < allDayAssignments.length;
                            aIdx++
                          ) {
                            const assignment = allDayAssignments[aIdx];
                            const aStart = dayjs(assignment.startDate);
                            const aEnd = dayjs(assignment.eddDate);
                            const startH = aStart.isSame(day, "day")
                              ? aStart.hour() + aStart.minute() / 60
                              : 0;
                            const endH = aEnd.isSame(day, "day")
                              ? aEnd.hour() + aEnd.minute() / 60
                              : 24;
                            if (endH <= startH) continue;
                            positioned.push({
                              topPx: startH * WEEK_DETAIL_HOUR_H,
                              heightPx: Math.max(
                                (endH - startH) * WEEK_DETAIL_HOUR_H,
                                24
                              ),
                              data: { ...assignment, _aIdx: aIdx }
                            });
                          }

                          const clusters = groupByTimeOverlap(positioned);

                          return clusters.flatMap((cluster, cIdx) => {
                            const total = cluster.length;
                            const visible = cluster.slice(0, 2);
                            const hidden = cluster.slice(2);
                            const hasOverflow = hidden.length > 0;

                            const availableW = colWidth - 2 * OUTER_PAD;
                            const barW =
                              total === 1
                                ? availableW
                                : hasOverflow
                                  ? (availableW - OVERFLOW_W - 2 * INNER_GAP) /
                                    2
                                  : (availableW - INNER_GAP) / 2;

                            const barElements = visible.map((item, idx) => {
                              const { data: assignment } = item;
                              const leftPx =
                                OUTER_PAD + idx * (barW + INNER_GAP);
                              const {
                                color: barColor,
                                text: barText,
                                accent: barAccent
                              } = getJobBarColorForTime(
                                assignment.startDate,
                                assignment.barVariant
                              );
                              return (
                                <ScheduleBarTooltip
                                  key={`${assignment.id}-${day.toISOString()}-${assignment._aIdx}`}
                                  variant="machine"
                                  data={{
                                    jobOrderNo: assignment.jobOrderNo,
                                    processDesc: assignment.processDesc,
                                    partNo: assignment.partNo,
                                    partDescription: assignment.partDescription,
                                    customerName: assignment.customerName,
                                    startDate: assignment.startDate,
                                    eddDate: assignment.eddDate,
                                    duration: assignment.duration,
                                    technicianName: assignment.technicianName,
                                    machineName: assignment.machineName,
                                    priorityColor: barColor,
                                    unavailableReason:
                                      assignment.barUnavailableReason
                                  }}
                                >
                                  <div
                                    className={getAssignmentBarClasses(
                                      barColor,
                                      false,
                                      false,
                                      false,
                                      barText,
                                      "dashed-full"
                                    )}
                                    style={{
                                      position: "absolute",
                                      top: item.topPx,
                                      left: leftPx,
                                      width: Math.max(barW, 24),
                                      height: item.heightPx,
                                      zIndex: 2
                                    }}
                                  >
                                    <div
                                      className={`aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] ${barAccent} aero:rounded-l`}
                                    />
                                    <TechnicianBarContent
                                      startDate={assignment.startDate}
                                      eddDate={assignment.eddDate}
                                      processDesc={assignment.processDesc}
                                      jobOrderNo={assignment.jobOrderNo}
                                      partNo={assignment.partNo}
                                      partDescription={
                                        assignment.partDescription
                                      }
                                      customerName={assignment.customerName}
                                      technicianName={assignment.technicianName}
                                      machineName={assignment.machineName}
                                      barHeight={item.heightPx}
                                      type="daily"
                                      textClass={barText}
                                      formatTime={formatTime}
                                    />
                                  </div>
                                </ScheduleBarTooltip>
                              );
                            });

                            if (!hasOverflow) return barElements;

                            // +N bar: span full cluster range, clamped to work hours 08:00â€“17:00.
                            // Case 1: bar 01:00â†’17:00 â†’ +N starts at 08:00 (not 01:00)
                            // Case 2: A 08:20â†’11:00, B 09:00â†’15:00 â†’ +N is 08:20â†’15:00 (natural)
                            // Case 3: A 01:00â†’11:00, B 05:00â†’13:00 â†’ +N is 08:00â†’13:00
                            const WORK_START_PX = 8 * WEEK_DETAIL_HOUR_H; // 320px
                            const WORK_END_PX = 17 * WEEK_DETAIL_HOUR_H; // 680px
                            const clusterTop = Math.min(
                              ...cluster.map((h) => h.topPx)
                            );
                            const clusterBottom = Math.max(
                              ...cluster.map((h) => h.topPx + h.heightPx)
                            );
                            const overflowTop = Math.max(
                              clusterTop,
                              WORK_START_PX
                            );
                            const overflowBottom = Math.min(
                              clusterBottom,
                              WORK_END_PX
                            );
                            const overflowLeft =
                              OUTER_PAD + 2 * (barW + INNER_GAP);

                            return [
                              ...barElements,
                              <OverflowIndicatorCard
                                key={`overflow-machine-${day.toISOString()}-${cIdx}`}
                                count={hidden.length}
                                top={overflowTop}
                                left={overflowLeft}
                                height={overflowBottom - overflowTop}
                                variant="machine"
                                items={hidden.map((h) => ({
                                  jobOrderNo: h.data.jobOrderNo,
                                  processDesc: h.data.processDesc,
                                  partNo: h.data.partNo,
                                  partDescription: h.data.partDescription,
                                  customerName: h.data.customerName ?? null,
                                  startDate: h.data.startDate,
                                  eddDate: h.data.eddDate,
                                  duration: h.data.duration,
                                  technicianName: h.data.technicianName,
                                  machineName: h.data.machineName,
                                  unavailableReason:
                                    h.data.barUnavailableReason,
                                  priorityColor: getJobBarColorForTime(
                                    h.data.startDate,
                                    h.data.barVariant
                                  ).color
                                }))}
                              />
                            ];
                          });
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Body rows â€” spanning bars (< 100% zoom) */}
              {!showDetailGrid &&
                rowItems.map((item, idx) => {
                  if (item.kind === "shop-header") {
                    return (
                      <div
                        key={`shr-${item.shopName}-${idx}`}
                        className={cn(
                          "aero:border-b aero:border-neutral-300",
                          idx === 0 && "aero:border-t aero:border-neutral-300",
                          item.isAnySelected
                            ? "aero:bg-brand-primary-50"
                            : "aero:bg-neutral-50"
                        )}
                        style={{
                          height: SHOP_HEADER_H,
                          backgroundImage:
                            "repeating-linear-gradient(45deg, rgba(115, 115, 115, 0.1) 0px, rgba(115, 115, 115, 0.1) 1px, transparent 1px, transparent 8px)"
                        }}
                      />
                    );
                  }

                  const { machine, rowHeight, assignmentsWithLanes } = item;

                  return (
                    <div
                      key={machine.machineId}
                      className="aero:relative aero:border-b aero:border-neutral-300 aero:last:border-b-0"
                      style={{ height: rowHeight }}
                    >
                      <SpanningGridLines days={days} viewMode="weekly" />
                      <AvailabilityBands
                        days={days}
                        availability={availabilityMap?.get(machine.machineId)}
                      />

                      {assignmentsWithLanes.map((assignment) => {
                        const barPos = getSpanningBarPosition(
                          assignment,
                          days,
                          "weekly",
                          zoomLevel
                        );
                        if (!barPos || !barPos.isVisible) return null;
                        const {
                          leftPosition,
                          barWidthStyle,
                          isStartClipped,
                          isEndClipped
                        } = barPos;
                        const lane = assignment.lane || 0;
                        const topOffset = ROW_PAD + lane * laneHeight;
                        const barHeight = laneHeight - ROW_PAD;
                        const {
                          color: barColor,
                          text: barText,
                          accent: barAccent
                        } = getJobBarColorForTime(
                          assignment.startDate,
                          assignment.barVariant
                        );

                        return (
                          <ScheduleBarTooltip
                            key={assignment.id}
                            variant="machine"
                            data={{
                              jobOrderNo: assignment.jobOrderNo,
                              processDesc: assignment.processDesc,
                              partNo: assignment.partNo,
                              partDescription: assignment.partDescription,
                              customerName: assignment.customerName,
                              startDate: assignment.startDate,
                              eddDate: assignment.eddDate,
                              duration: assignment.duration,
                              technicianName: assignment.technicianName,
                              machineName: assignment.machineName,
                              priorityColor: barColor,
                              unavailableReason: assignment.barUnavailableReason
                            }}
                          >
                            <div
                              className={getAssignmentBarClasses(
                                barColor,
                                isStartClipped,
                                isEndClipped,
                                false,
                                barText,
                                "dashed-full"
                              )}
                              style={{
                                left: leftPosition,
                                width: barWidthStyle,
                                top: `${topOffset}px`,
                                height: `${barHeight}px`,
                                zIndex: 2
                              }}
                            >
                              {isStartClipped && (
                                <ClippedIndicator position="start" />
                              )}
                              {isEndClipped && (
                                <ClippedIndicator position="end" />
                              )}
                              {!isStartClipped && (
                                <div
                                  className={`aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] ${barAccent} aero:rounded-l`}
                                />
                              )}
                              <TechnicianBarContent
                                startDate={assignment.startDate}
                                eddDate={assignment.eddDate}
                                processDesc={assignment.processDesc}
                                jobOrderNo={assignment.jobOrderNo}
                                partNo={assignment.partNo}
                                partDescription={assignment.partDescription}
                                customerName={assignment.customerName}
                                technicianName={assignment.technicianName}
                                machineName={assignment.machineName}
                                barHeight={barHeight}
                                type="weekly"
                                textClass={barText}
                                formatTime={formatTime}
                              />
                            </div>
                          </ScheduleBarTooltip>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  );
}

// â”€â”€ MonthlyMachineCalendarGrid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Thin wrapper â€” delegates to the shared MonthlyCalendarGrid.
// Maps each machine into a MonthlyCalendarRow
// and provides TechnicianBarContent as the bar renderer.

interface MonthlyMachineCalendarGridProps {
  days: Date[];
  /** Machines to merge into the calendar (already narrowed by the toolbar Show picker). */
  machines: ProcessedMachine[];
  zoomLevel?: number;
  formatTime: (date: string | null | undefined) => string;
  /** Per-machine AM/PM availability — aggregated into the merged calendar (WOR-2187 R3). */
  availabilityMap?: AvailabilityMap;
}

export function MonthlyMachineCalendarGrid({
  days,
  machines,
  zoomLevel = ZOOM_MIN,
  formatTime,
  availabilityMap
}: MonthlyMachineCalendarGridProps) {
  const rows = useMemo<MonthlyCalendarRow[]>(
    () =>
      machines.map((machine) => ({
        id: machine.machineId,
        assignments: machine.assignedJobsWithPriority
      })),
    [machines]
  );

  return (
    <MonthlyCalendarGrid
      days={days}
      rows={rows}
      zoomLevel={zoomLevel}
      availabilityMap={availabilityMap}
      renderBarContent={({ assignment: a, barHeight }) => (
        <TechnicianBarContent
          startDate={a.startDate}
          eddDate={a.eddDate}
          processDesc={a.processDesc}
          jobOrderNo={a.jobOrderNo}
          partNo={a.partNo}
          partDescription={a.partDescription}
          customerName={a.customerName}
          technicianName={a.technicianName}
          machineName={a.machineName}
          barHeight={barHeight}
          type="monthly"
          textClass={getJobBarColorForTime(a.startDate, a.barVariant).text}
          formatTime={formatTime}
        />
      )}
    />
  );
}

// MonthlyMachineCalendarGrid now delegates to the shared MonthlyCalendarGrid.
